import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'wouter';

import usePrefersReducedMotion from '../../hooks/usePrefersReducedMotion';
import useWindowSize from '../../hooks/useWindowSize';

import { useModal } from '../../contexts/ModalContext';
import backgroundh264portrait from '../../videos/FOND_KIOSQUE_9x16.mp4';
import backgroundh264wide from '../../videos/FOND_KIOSQUE_16x9.mp4';
import Button from '../buttons/Button';
import Cookie from '../icons/Cookie';
import Consent from '../modals/Consent';

import logoWhite from '../../img/la-poste-white.png';
import logo from '../../img/la-poste.png';
import styles from '../../styles/layouts/main.module.css';
import backgroundwebmportrait from '../../videos/FOND_KIOSQUE_9x16.webm';
import backgroundwebmwide from '../../videos/FOND_KIOSQUE_16x9.webm';

interface MainLayoutProps {
    fullScreen?: boolean;
    children?: React.ReactNode;
}

function MainLayout({ fullScreen = false, children = null }: MainLayoutProps) {
    const route = useUrlGenerator();
    const { modal = null, setModal = null } = useModal();

    const [consentOpenedByUser, setConsentOpenedByUser] = useState(false);
    const onClickCookie = useCallback(() => {
        setConsentOpenedByUser(true);
        if (setModal !== null) {
            setModal('consent');
        }
    }, [setModal, setConsentOpenedByUser]);

    const { width = 0, height = 0 } = useWindowSize();
    const widescreen = width / height >= 1;

    const large = useMemo(() => {
        return width >= 900;
    }, [width]);

    const video = useMemo(() => {
        return widescreen
            ? {
                  h264: backgroundh264wide,
                  webm: backgroundwebmwide,
              }
            : {
                  h264: backgroundh264portrait,
                  webm: backgroundwebmportrait,
              };
    }, [widescreen]);

    // La vidéo de fond est purement décorative et tourne en boucle : elle est
    // masquée aux technologies d'assistance et coupée quand l'internaute a
    // demandé à réduire les animations (WCAG 2.2.2 / 2.3.3).
    const reducedMotion = usePrefersReducedMotion();
    const videoRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        const videoElement = videoRef.current;
        if (reducedMotion && videoElement !== null) {
            videoElement.pause();
        }
    }, [reducedMotion]);

    // Dans une application monopage, le changement de route ne déplace pas le
    // focus : on le repositionne sur le contenu principal pour que la
    // navigation au clavier et les lecteurs d'écran suivent (WCAG 2.4.3).
    // Le mode plein écran est exclu : le visionneur y change l'URL à chaque
    // écran du micromag.
    const [location] = useLocation();
    const mainRef = useRef<HTMLElement>(null);
    const previousLocation = useRef<string | null>(null);

    useEffect(() => {
        const { current: previous } = previousLocation;
        previousLocation.current = location;
        if (fullScreen || previous === null || previous === location) {
            return;
        }
        const mainElement = mainRef.current;
        if (mainElement !== null) {
            mainElement.focus();
        }
    }, [location, fullScreen]);

    return (
        <div
            className={classNames([styles.container, { [styles.fullScreen]: fullScreen === true }])}
        >
            <a className="skip-link" href="#main-content">
                Aller au contenu principal
            </a>
            <header className={styles.header}>
                <a
                    className={styles.logoLink}
                    href="https://www.lapostegroupe.com"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <img className={styles.logo} src={logo} alt="Groupe La Poste" />
                    <span className="visually-hidden"> (nouvelle fenêtre)</span>
                </a>
                <Link className={styles.titleLink} href="/">
                    <p className={styles.title}>
                        Les postiers
                        {large ? <br /> : ' '}
                        de l'année 2025
                    </p>
                </Link>
            </header>
            <main className={styles.inner} id="main-content" ref={mainRef} tabIndex={-1}>
                <div className={styles.content}>{children}</div>
            </main>
            <footer className={styles.footer}>
                <nav className={styles.items} aria-label="Liens de bas de page">
                    <Link className={styles.link} href={route('legal')} transition>
                        Mentions légales
                    </Link>{' '}
                    <Link className={styles.link} href={route('accessibility')} transition>
                        Accessibilité&nbsp;: non conforme
                    </Link>{' '}
                    <a
                        className={styles.link}
                        href="https://www.lapostegroupe.com/fr/politique-de-protection-des-donnees-personnelles"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Politique de données
                        <span className="visually-hidden"> (nouvelle fenêtre)</span>
                    </a>
                    <button
                        className={styles.link}
                        id="consent-trigger-footer"
                        type="button"
                        onClick={onClickCookie}
                    >
                        Gérer les cookies
                    </button>
                </nav>
                <a
                    className={styles.logoLink}
                    href="https://www.lapostegroupe.com"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <img className={styles.logo} src={logoWhite} alt="Groupe La Poste" />
                    <span className="visually-hidden"> (nouvelle fenêtre)</span>
                </a>
            </footer>
            <video
                className={styles.video}
                ref={videoRef}
                autoPlay={!reducedMotion}
                loop
                muted
                playsInline
                preload="auto"
                aria-hidden="true"
                tabIndex={-1}
            >
                <source src={`${video.h264}#t=0.01`} type="video/mp4" />
                <source src={`${video.webm}#t=0.01`} type="video/webm" />
            </video>
            {modal === 'consent' ? (
                <Consent
                    className={styles.consentModal}
                    returnFocusId={consentOpenedByUser ? 'consent-trigger' : null}
                />
            ) : (
                <Button
                    className={styles.cookies}
                    id="consent-trigger"
                    ariaLabel="Gérer les cookies"
                    onClick={onClickCookie}
                >
                    <Cookie className={styles.cookieIcon} />
                </Button>
            )}
        </div>
    );
}

export default MainLayout;
